import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { createRecord, getRecord, updateRecord } from '@/api/records';
import { listCategories } from '@/api/categories';
import { recordFormSchema, type RecordFormValues } from '@/schemas';
import { Spinner } from '@/components/feedback/Spinner';

interface Props {
  mode: 'new' | 'edit';
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeBmiPreview(heightCm: string | null | undefined, weightKg: string | null | undefined): number | null {
  const h = heightCm ? Number(heightCm) : NaN;
  const w = weightKg ? Number(weightKg) : NaN;
  if (!h || !w || Number.isNaN(h) || Number.isNaN(w) || h <= 0) return null;
  const m = h / 100;
  return w / (m * m);
}

export function RecordEditPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = useParams<{ id: string }>();
  const id = mode === 'edit' ? Number(params.id) : undefined;

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const recordQuery = useQuery({
    queryKey: ['records', id],
    queryFn: () => getRecord(id!),
    enabled: mode === 'edit' && Number.isFinite(id),
  });

  const defaultValues: RecordFormValues = useMemo(
    () => ({
      recordDate: todayIso(),
      heightCm: '' as unknown as string | null,
      weightKg: '' as unknown as string | null,
      categoryId: null,
      notes: '',
    }),
    []
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RecordFormValues>({
    resolver: zodResolver(recordFormSchema),
    defaultValues,
  });

  // When the loaded record arrives, reset the form
  useEffect(() => {
    if (mode === 'edit' && recordQuery.data) {
      const r = recordQuery.data;
      reset({
        recordDate: r.recordDate,
        heightCm: r.heightCm ?? ('' as unknown as string | null),
        weightKg: r.weightKg ?? ('' as unknown as string | null),
        categoryId: r.categoryId ?? null,
        notes: r.notes ?? '',
      });
    }
  }, [mode, recordQuery.data, reset]);

  const createMutation = useMutation({
    mutationFn: createRecord,
    onSuccess: () => {
      toast.success(t('records.saveSuccess'));
      qc.invalidateQueries({ queryKey: ['records'] });
      navigate('/records', { replace: true });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: RecordFormValues) =>
      updateRecord(id!, {
        recordDate: input.recordDate,
        heightCm: input.heightCm || null,
        weightKg: input.weightKg || null,
        categoryId: input.categoryId ?? null,
        notes: input.notes || null,
      }),
    onSuccess: () => {
      toast.success(t('records.saveSuccess'));
      qc.invalidateQueries({ queryKey: ['records'] });
      qc.invalidateQueries({ queryKey: ['records', id] });
      navigate('/records', { replace: true });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const watchedH = watch('heightCm');
  const watchedW = watch('weightKg');
  const bmiPreview = computeBmiPreview(watchedH as unknown as string, watchedW as unknown as string);

  if (mode === 'edit' && recordQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="md" />
      </div>
    );
  }

  if (mode === 'edit' && recordQuery.isError) {
    return (
      <div className="card p-6 text-sm text-rose-600">
        {(recordQuery.error as Error).message}
      </div>
    );
  }

  async function onSubmit(values: RecordFormValues) {
    const payload = {
      recordDate: values.recordDate,
      heightCm: values.heightCm ? String(values.heightCm) : null,
      weightKg: values.weightKg ? String(values.weightKg) : null,
      categoryId: values.categoryId ?? null,
      notes: values.notes || null,
    };
    if (mode === 'new') {
      await createMutation.mutateAsync(payload);
    } else {
      await updateMutation.mutateAsync(values);
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/records" className="btn-secondary px-2 py-1" aria-label={t('common.back')}>
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {mode === 'new' ? t('records.newTitle') : t('records.editTitle')}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-4" noValidate>
        <div>
          <label htmlFor="recordDate" className="block text-sm font-medium mb-1">
            {t('records.form.date')}
          </label>
          <input id="recordDate" type="date" className="input" {...register('recordDate')} />
          {errors.recordDate && (
            <p className="mt-1 text-xs text-rose-600">{errors.recordDate.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="heightCm" className="block text-sm font-medium mb-1">
              {t('records.form.height')}
            </label>
            <input
              id="heightCm"
              type="text"
              inputMode="decimal"
              placeholder="175"
              className="input"
              {...register('heightCm')}
            />
            {errors.heightCm && (
              <p className="mt-1 text-xs text-rose-600">{errors.heightCm.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="weightKg" className="block text-sm font-medium mb-1">
              {t('records.form.weight')}
            </label>
            <input
              id="weightKg"
              type="text"
              inputMode="decimal"
              placeholder="72.5"
              className="input"
              {...register('weightKg')}
            />
            {errors.weightKg && (
              <p className="mt-1 text-xs text-rose-600">{errors.weightKg.message}</p>
            )}
          </div>
        </div>

        {bmiPreview != null && Number.isFinite(bmiPreview) && (
          <div className="rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm flex items-center justify-between">
            <span className="text-slate-600 dark:text-slate-400">{t('records.columns.bmi')}</span>
            <span className="font-semibold tabular-nums">{bmiPreview.toFixed(1)}</span>
          </div>
        )}

        <div>
          <label htmlFor="categoryId" className="block text-sm font-medium mb-1">
            {t('records.form.category')}
          </label>
          <Controller
            control={control}
            name="categoryId"
            render={({ field }) => (
              <select
                id="categoryId"
                className="input"
                value={field.value == null ? '' : String(field.value)}
                onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{t('records.form.noCategory')}</option>
                {(categoriesQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          />
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium mb-1">
            {t('records.form.notes')}
          </label>
          <textarea
            id="notes"
            rows={4}
            placeholder={t('records.form.notesPlaceholder')}
            className="input"
            {...register('notes')}
          />
        </div>

        <p className="text-xs text-slate-500">{t('records.bmiAuto')}</p>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Link to="/records" className="btn-secondary">
            {t('records.form.cancel')}
          </Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
          >
            {isSubmitting || createMutation.isPending || updateMutation.isPending
              ? t('records.form.saving')
              : t('records.form.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
